import { useEffect, useCallback, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useIntl } from 'react-intl';
import { CheckoutPage } from '@repo/domain-kit/payment-links';
import window from 'ember-window-mock';
import { useToast } from '@repo/design-system-kit';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { useMollie } from 'receivable-invoices-js/react/hooks/use-mollie';
import { useCreatePayment } from 'receivable-invoices-js/react/api/mutations/create-payment';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import { isDeviceApplePayEnabled } from 'receivable-invoices-js/react/utils/apple-pay';
import { Loading } from 'receivable-invoices-js/react/components/shared/loading';
import { useRouteQuery } from './use-route-query';
import { useStatusPolling } from './hooks/use-status-polling';

export function PaymentLinksRoute(): ReactNode {
  const [, setSearchParams] = useSearchParams();
  const { formatMessage, locale } = useIntl();
  const sentry = useSentry();
  const mollie = useMollie();
  const { showToast } = useToast();

  const { paymentLink, isLoading, isError, paymentId } = useRouteQuery();
  const pollingStatus = useStatusPolling({ paymentId, enabled: Boolean(paymentId) });

  const { mutate: createPayment, isPending: isLoadingPayment } = useCreatePayment({
    onSuccess: paymentLinkUrl => {
      if (paymentLinkUrl) {
        window.location.href = paymentLinkUrl;
      }
    },
    onError: (error: unknown) => {
      if (shouldSendToSentry(error)) {
        sentry.captureException(error);
      }

      showToast({
        text: formatMessage({ id: 'toasts.errors.try-later' }),
        type: 'error',
      });
    },
  });

  // Load Mollie after fetching payment link
  useEffect(() => {
    if (paymentLink?.profileId) {
      void mollie.load(paymentLink.profileId);
    }
  }, [paymentLink?.profileId, mollie]);

  const handleCheckout = useCallback(
    ({ method, email, token }: { method: string; email: string; token?: string }) => {
      if (!paymentLink?.paymentInitiator.resourceId) return;

      createPayment({
        resourceId: paymentLink.paymentInitiator.resourceId,
        paymentMethod: method,
        creditCardToken: token,
        userEmail: email,
        locale,
      });
    },
    [paymentLink, createPayment, locale]
  );

  const handleRetry = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('payment_id');
      return newParams;
    });
  }, [setSearchParams]);

  const handleRetryMollieError = useCallback(() => {
    window.location.reload();
  }, []);

  if (isLoading) {
    return <Loading spinnerSize="large" />;
  }

  if (
    isError ||
    !paymentLink ||
    paymentLink.status === 'expired' ||
    paymentLink.status === 'canceled'
  ) {
    return (
      <Navigate
        replace
        state={{
          title: formatMessage({ id: 'error-states.payment-links-expired.title' }),
          subtitle: formatMessage({ id: 'error-states.payment-links-expired.subtitle' }),
        }}
        to="/404"
      />
    );
  }

  return (
    <CheckoutPage
      disabled={isLoadingPayment}
      hasMollieError={mollie.hasError}
      hasNewPayment={Boolean(paymentId)}
      isApplePayAvailable={isDeviceApplePayEnabled()}
      isError={pollingStatus === 'failure'}
      isPolling={['idle', 'polling', 'timeout'].includes(pollingStatus)}
      mollie={mollie.isLoaded ? mollie : null}
      onCheckout={handleCheckout}
      onMollieErrorRetry={handleRetryMollieError}
      onRetry={handleRetry}
      paymentLink={paymentLink}
    />
  );
}
